import { useApi } from '@/contexts/ApiContext'

// Helper to unwrap Axios responses (data field) and gracefully handle undefined
const unwrap = async <T = any>(promise: Promise<any>): Promise<T> => {
  const res = await promise
  return (res && res.data) ?? res
}

/**
 * Adapter between BalancePage и новым сгенерированным BalanceApi.
 * Сохраняет названия методов, которые уже использует страница, поэтому
 * замену можно сделать без правок UI.
 */
export function useBalanceService() {
  const { BalanceApi } = useApi()

  /* ----------------------------- READ OPERATIONS ---------------------------- */
  const getBalances = async (limit = 100, offset = 0) => {
    const res: any = await unwrap(BalanceApi.balanceListGet(undefined, limit, offset))
    // API возвращает { balances, pagination }. UI ждёт массив Balances
    return res.balances ?? []
  }

  const listCurrencies = async (params: { onlyEnabled?: boolean } = {}) => {
    const { onlyEnabled } = params
    return unwrap(BalanceApi.balanceCurrenciesGet(undefined, undefined, onlyEnabled))
  }

  const getTransactions = async (params: { limit?: number; offset?: number } = {}) => {
    const { limit = 50, offset = 0 } = params
    return unwrap(BalanceApi.balanceTransactionsGet(undefined, limit, offset))
  }

  const listCryptoAddresses = async (params: { limit?: number; offset?: number } = {}) => {
    const { limit = 100, offset = 0 } = params
    return unwrap(BalanceApi.balanceCryptoAddressesGet(limit, offset))
  }

  const listFiatDetails = async (params: { limit?: number; offset?: number } = {}) => {
    const { limit = 100, offset = 0 } = params
    return unwrap(BalanceApi.balanceFiatDetailsGet(limit, offset))
  }

  /* ----------------------------- WRITE OPERATIONS --------------------------- */
  const createDeposit = (payload: any) => unwrap(BalanceApi.balanceDepositPost(payload))

  const createWithdrawal = (payload: any) => {
    const fn = (BalanceApi as any).balanceWithdrawalPost
    if (fn) return unwrap(fn.call(BalanceApi, payload))
    return Promise.reject(new Error('Withdrawal endpoint not implemented'))
  }

  const createCryptoAddress = (payload: any) => {
    const fn = (BalanceApi as any).balanceCryptoAddressesPost
    if (fn) return unwrap(fn.call(BalanceApi, payload))
    return Promise.reject(new Error('Create crypto address endpoint not implemented'))
  }

  const createFiatDetails = (payload: any) => unwrap(BalanceApi.balanceFiatDetailsPost(payload))

  const ensureBalancesForUser = (_userId: string | undefined, currencies: string[]) => {
    return unwrap(BalanceApi.balanceEnsurePost({ currencies }))
  }

  return {
    /* reads */
    getBalances,
    listCurrencies,
    getTransactions,
    listCryptoAddresses,
    listFiatDetails,

    /* writes */
    createDeposit,
    createWithdrawal,
    createCryptoAddress,
    createFiatDetails,
    ensureBalancesForUser,
  }
} 